/**
 * Sealed EI blast radius — the one reader of `pkg.blast_radius`.
 *
 * The map changed shape over time: packages sealed before the patch-scoped
 * analysis carry only a file-count level (`risk_level`), the patched paths
 * under the misleading key `services`, a free-text `notes` string and,
 * when the name-match refresh ran, `graph_risk_level` / `affected_count` /
 * an unsorted, unflagged `affected_entities` slice. Newer packages carry an
 * explicit status, method and an authoritative `risk_level`. Every screen
 * that shows the blast radius reads it through this module so that the two
 * shapes can never render differently, and so that a radius nobody computed
 * is never shown as "low".
 */
import type {
  EiBlastRadius,
  EiImplementationPackage,
} from '../types/engineeringIntelligence';
import type { StatusTone } from '../design-system/foundation';
import { riskLevelRank } from './engineeringRiskPresentation';

export type BlastStatus = 'computed' | 'partial' | 'not_computed' | 'not_applicable';

/** What kind of claim an affected entity is. Only name matches are hints;
 * every other class rests on a resolved edge or an area analyser's finding. */
export type BlastEvidenceClass = 'call_graph' | 'area' | 'knowledge_graph' | 'changed' | 'name_match';

export interface BlastEntityVm {
  name: string;
  path?: string;
  depth?: number;
  kind: string;
  resolution?: string;
  evidenceClass: BlastEvidenceClass;
  /** "call graph", "config read", "name-matched", ... */
  evidenceLabel: string;
  evidence?: string;
}

export interface BlastEntityGroupVm {
  key: string;
  label: string;
  entities: BlastEntityVm[];
}

export interface BlastChangedSymbolVm {
  name: string;
  file?: string;
  kind?: string;
  change: string;
  oldName?: string;
  callers?: number;
  publicApi: boolean;
  /** Removed, renamed or re-signed while resolved callers remain outside the patch. */
  breaksCallers: boolean;
}

export interface BlastAreaVm {
  area: string;
  areaLabel: string;
  path?: string;
  status: string;
  reason?: string;
  keys: string[];
  entityCount?: number;
  riskFloor?: string;
  security: boolean;
  operational: boolean;
  factors: string[];
}

/** One area's verdict over the whole patch, listed even when the area found
 * nothing — so "not checked" and "checked, nothing reached" never look alike. */
export interface BlastAreaCoverageVm {
  area: string;
  areaLabel: string;
  status: string;
  /** "Affected", "Checked — no impact found", "Incomplete", ... */
  statusLabel: string;
  tone: StatusTone;
  files: number;
  entities: number;
  detail?: string;
  riskFloor?: string;
}

export interface BlastRadiusVm {
  scope: string;
  /** True when the radius was seeded from the sealed patch's own paths. */
  patchScoped: boolean;
  /** False for packages sealed before status/method were recorded. */
  structured: boolean;
  status: BlastStatus;
  /** "Computed", "Partial — <reason>", "Not computed — <reason>", ... */
  statusLabel: string;
  reason?: string;
  method?: string;
  methodLabel?: string;
  /** Authoritative level: low|medium|high|critical|unknown|not_applicable.
   * Never defaulted to "low" — a missing grade is "unknown". */
  riskLevel: string;
  /** Why the level is unknown, when it is. */
  riskReason?: string;
  /** "High", "Unknown — <reason>", "Not applicable — <reason>". */
  riskLabel: string;
  pathCountRiskLevel?: string;
  graphRiskLevel?: string;
  /** The backend's sentence; omitted for legacy packages, whose summary
   * embeds the graph level even when the path count graded higher. */
  summary?: string;
  seeds: string[];
  changedSymbols: BlastChangedSymbolVm[];
  directCallers: BlastEntityVm[];
  directCallerCount: number;
  entityGroups: BlastEntityGroupVm[];
  shownEntityCount: number;
  /** Uncapped number of affected entities. */
  affectedCount: number;
  truncated: boolean;
  /** "Showing 24 of 300" when the stored list is a cut of the total. */
  truncationLabel?: string;
  nameMatchedCount: number;
  resolvedCount: number;
  areas: BlastAreaVm[];
  /** Every area checked; empty for packages sealed before coverage existed. */
  areaCoverage: BlastAreaCoverageVm[];
  unresolved: Array<{ path: string; reason: string }>;
  affectedTests: string[];
  dataDependentTests: string[];
  testSelection?: string;
  modifiedTests: Array<{ path: string; kind: string; detail?: string }>;
  generatedFiles: string[];
  publicApiChanges: string[];
  riskFactors: string[];
  notes: string[];
  indexedFiles?: number;
  edgeCount?: number;
  confidence?: number;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function strList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => (typeof x === 'string' ? x.trim() : '')).filter(Boolean);
}

function objList(v: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is Record<string, unknown> => Boolean(x) && typeof x === 'object' && !Array.isArray(x));
}

function humanize(s: string): string {
  const t = s.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return t ? t[0].toUpperCase() + t.slice(1) : t;
}

/** The package's blast radius map, or undefined when there is none (or only
 * the legacy array form, which carries no analysis). */
export function readBlastRadius(pkg: EiImplementationPackage | null | undefined): EiBlastRadius | undefined {
  const br = pkg?.blast_radius as unknown;
  if (!br || typeof br !== 'object' || Array.isArray(br)) return undefined;
  return br as EiBlastRadius;
}

/** `notes` is string[] on current packages and one free-text string on older ones. */
export function blastNotes(br: EiBlastRadius | undefined): string[] {
  const notes = br?.notes as unknown;
  if (typeof notes === 'string') return notes.trim() ? [notes.trim()] : [];
  return strList(notes);
}

const METHOD_LABELS: Record<string, string> = {
  symbol_graph: 'Call graph',
  name_match: 'Name match (hints only)',
  mixed: 'Call graph + name match',
  path_count: 'Patched-file count only',
};

export function blastMethodLabel(method?: string): string | undefined {
  if (!method) return undefined;
  return METHOD_LABELS[method] ?? humanize(method);
}

const RESOLUTION_CLASSES: Record<string, { cls: BlastEvidenceClass; label: string }> = {
  call_edge: { cls: 'call_graph', label: 'call graph' },
  data_edge: { cls: 'call_graph', label: 'data flow' },
  test_edge: { cls: 'call_graph', label: 'test edge' },
  calls_removed_symbol: { cls: 'call_graph', label: 'calls removed symbol' },
  import_edge: { cls: 'area', label: 'imports changed module' },
  config_read: { cls: 'area', label: 'config read' },
  sql_reference: { cls: 'area', label: 'SQL reference' },
  route_match: { cls: 'area', label: 'route match' },
  topic_match: { cls: 'area', label: 'topic match' },
  runtime_manifest: { cls: 'area', label: 'runtime manifest' },
  file_reference: { cls: 'area', label: 'loads the file by name' },
  kg_edge: { cls: 'knowledge_graph', label: 'knowledge graph' },
  changed: { cls: 'changed', label: 'changed by the patch' },
  name_match: { cls: 'name_match', label: 'name-matched' },
};

const AREA_LABELS: Record<string, string> = {
  code: 'Code (call graph)',
  api_contract: 'API contract',
  config: 'Configuration',
  database: 'Database',
  dependency: 'Dependencies',
  runtime: 'Runtime & deployment',
  queue: 'Queues & topics',
  mule: 'Mule flows',
  schema: 'Schemas (proto, GraphQL, Avro)',
  test: 'Tests',
  generated: 'Generated code',
  docs: 'Docs & assets',
  reference: 'Files loaded by name',
};

export function blastAreaLabel(area: string): string {
  return AREA_LABELS[area] ?? humanize(area);
}

const COVERAGE_STATUS: Record<string, { label: string; tone: StatusTone }> = {
  affected: { label: 'Affected', tone: 'info' },
  clear: { label: 'Checked — no impact found', tone: 'success' },
  incomplete: { label: 'Incomplete — part could not be placed', tone: 'warning' },
  not_applicable: { label: 'Not applicable', tone: 'neutral' },
  not_involved: { label: 'Checked — no changed file of this kind', tone: 'neutral' },
  not_run: { label: 'Not run', tone: 'warning' },
};

/** Every area's verdict, in the backend's order (the call graph first). */
export function readAreaCoverage(br: EiBlastRadius | undefined): BlastAreaCoverageVm[] {
  return objList(br?.area_coverage)
    .map((o): BlastAreaCoverageVm | null => {
      const area = str(o.area);
      if (!area) return null;
      const status = str(o.status) || 'incomplete';
      const s = COVERAGE_STATUS[status] ?? { label: humanize(status), tone: 'warning' as StatusTone };
      return {
        area,
        areaLabel: blastAreaLabel(area),
        status,
        statusLabel: s.label,
        tone: s.tone,
        files: num(o.files) ?? 0,
        entities: num(o.entities) ?? 0,
        detail: str(o.detail),
        riskFloor: str(o.risk_floor),
      };
    })
    .filter((c): c is BlastAreaCoverageVm => c != null);
}

/** Labels a finding by its evidence. An entity without a resolution comes
 * from name matching unless the whole radius was a call-graph traversal —
 * that is how every package sealed before the label existed was built. */
export function classifyBlastEvidence(
  resolution: string | undefined,
  method: string | undefined,
): { cls: BlastEvidenceClass; label: string } {
  const r = (resolution || '').trim().toLowerCase();
  if (r) return RESOLUTION_CLASSES[r] ?? { cls: 'area', label: r.replace(/_/g, ' ') };
  if (method === 'symbol_graph') return RESOLUTION_CLASSES.call_edge;
  return RESOLUTION_CLASSES.name_match;
}

interface KindGroup {
  key: string;
  label: string;
}

const KIND_GROUPS: Array<{ group: KindGroup; kinds: string[] }> = [
  { group: { key: 'api', label: 'APIs & routes' }, kinds: ['api', 'route', 'endpoint', 'http', 'operation'] },
  { group: { key: 'service', label: 'Services' }, kinds: ['service', 'application', 'app', 'deployment'] },
  { group: { key: 'data', label: 'Data stores' }, kinds: ['database', 'db', 'table', 'column', 'schema', 'migration', 'sql'] },
  { group: { key: 'queue', label: 'Queues & topics' }, kinds: ['queue', 'topic', 'stream', 'channel', 'event'] },
  { group: { key: 'config', label: 'Configuration' }, kinds: ['config', 'configuration', 'env', 'setting', 'secret'] },
  { group: { key: 'runtime', label: 'Runtime & deployment' }, kinds: ['runtime', 'container', 'manifest', 'infrastructure', 'workflow', 'flow', 'job'] },
  {
    group: { key: 'symbol', label: 'Code symbols' },
    kinds: ['symbol', 'function', 'method', 'class', 'type', 'struct', 'interface', 'const', 'var', 'field', 'enum', 'type_alias', 'constructor', 'component'],
  },
  { group: { key: 'file', label: 'Files & modules' }, kinds: ['file', 'module', 'package', 'dependency', 'library'] },
  { group: { key: 'test', label: 'Tests' }, kinds: ['test', 'test_case', 'spec'] },
];

const GROUP_ORDER = [...KIND_GROUPS.map((k) => k.group.key), 'other'];

export function blastKindGroup(kind: string): KindGroup {
  const k = (kind || '').trim().toLowerCase();
  for (const g of KIND_GROUPS) {
    if (g.kinds.includes(k)) return g.group;
  }
  return { key: 'other', label: 'Other' };
}

/** Group keys whose names read as the change's scope chips. */
export const SCOPE_GROUPS = {
  services: ['service'],
  apis: ['api'],
  infrastructure: ['data', 'queue', 'config', 'runtime'],
} as const;

function toEntity(o: Record<string, unknown>, method: string | undefined): BlastEntityVm | null {
  const name = str(o.name) || str(o.path);
  if (!name) return null;
  const resolution = str(o.resolution);
  const ev = classifyBlastEvidence(resolution, method);
  return {
    name,
    path: str(o.path),
    depth: num(o.depth),
    kind: str(o.kind) || 'entity',
    resolution,
    evidenceClass: ev.cls,
    evidenceLabel: ev.label,
    evidence: str(o.evidence),
  };
}

const RISK_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

/** "Unknown — <reason>", never a bare or defaulted "low". */
export function blastRiskLabel(level: string, reason?: string): string {
  const l = (level || '').toLowerCase();
  if (RISK_LABELS[l]) return RISK_LABELS[l];
  if (l === 'not_applicable') return `Not applicable — ${reason || 'no code paths touched'}`;
  return `Unknown — ${reason || 'the change could not be traced through the repository'}`;
}

function statusLabel(status: BlastStatus, reason?: string): string {
  switch (status) {
    case 'computed':
      return 'Computed';
    case 'partial':
      return `Partial — ${reason || 'part of the change could not be analysed'}`;
    case 'not_applicable':
      return `Not applicable — ${reason || 'no code paths touched'}`;
    default:
      return `Not computed — ${reason || 'no dependency analysis ran for this patch'}`;
  }
}

function normalizeStatus(s: string | undefined): BlastStatus | undefined {
  switch ((s || '').toLowerCase()) {
    case 'computed':
      return 'computed';
    case 'partial':
      return 'partial';
    case 'not_computed':
      return 'not_computed';
    case 'not_applicable':
      return 'not_applicable';
    default:
      return undefined;
  }
}

function higherOf(a: string | undefined, b: string | undefined): string | undefined {
  const ra = riskLevelRank(a);
  const rb = riskLevelRank(b);
  if (ra === 0 && rb === 0) return undefined;
  return ra >= rb ? a!.toLowerCase() : b!.toLowerCase();
}

/**
 * Grade and status for a package sealed before the backend recorded them.
 * Its `risk_level` is the file-count heuristic and its graph level came from
 * substring matching, so: never downgrade below the file count, and a file
 * count of "low" with no traversal (or a traversal that matched nothing)
 * is unknown, not low.
 */
function legacyAssessment(br: EiBlastRadius, seeds: string[]): {
  status: BlastStatus;
  reason?: string;
  method: string;
  riskLevel: string;
  riskReason?: string;
} {
  const pathCount = str(br.risk_level);
  const graph = str(br.graph_risk_level);
  const affected = num(br.affected_count);
  if (seeds.length === 0) {
    return {
      status: 'not_applicable',
      reason: blastNotes(br)[0] || 'no code paths touched',
      method: 'path_count',
      riskLevel: 'not_applicable',
    };
  }
  const floor = riskLevelRank(pathCount) >= 2 ? pathCount!.toLowerCase() : undefined;
  if (affected == null && !graph) {
    const reason = 'only the number of patched files was assessed; no dependency analysis ran';
    return { status: 'not_computed', reason, method: 'path_count', riskLevel: floor ?? 'unknown', riskReason: floor ? undefined : reason };
  }
  if (!affected) {
    const reason = 'no repository entity matched the patched paths';
    return { status: 'partial', reason, method: 'name_match', riskLevel: floor ?? 'unknown', riskReason: floor ? undefined : reason };
  }
  return { status: 'computed', method: 'name_match', riskLevel: higherOf(pathCount, graph) ?? 'unknown' };
}

/** Builds the display model, or undefined when the package has no map. */
export function buildBlastRadiusVm(pkg: EiImplementationPackage | null | undefined): BlastRadiusVm | undefined {
  const br = readBlastRadius(pkg);
  if (!br) return undefined;
  const scope = str(br.scope) || 'none';
  // Legacy maps name the patched paths under "services"; the oldest ones
  // were a constant that named nothing, so fall back to the patch itself.
  const patchPaths = (pkg?.engineering_patch?.files ?? []).map((f) => (f.path || '').trim()).filter(Boolean);
  const seeds = [strList(br.seeds), strList(br.services), patchPaths].find((l) => l.length > 0) ?? [];
  const explicitStatus = normalizeStatus(str(br.status));
  const structured = Boolean(explicitStatus);

  let status: BlastStatus;
  let reason: string | undefined;
  let method: string | undefined;
  let riskLevel: string;
  let riskReason: string | undefined;
  if (explicitStatus) {
    status = explicitStatus;
    reason = str(br.reason);
    method = str(br.method);
    // A missing grade is unknown. Defaulting it to the lowest level is the
    // exact failure this model exists to prevent.
    riskLevel = (str(br.risk_level) || 'unknown').toLowerCase();
    // With no traversal, "low" can only be the file count, which says
    // nothing about reach; medium and above stay as a floor.
    if (status === 'not_computed' && riskLevel === 'low') riskLevel = 'unknown';
    if (riskLevel === 'unknown' || riskLevel === 'not_applicable') riskReason = reason;
  } else if (scope !== 'patch_paths' && scope !== 'none') {
    status = 'not_applicable';
    reason = scope === 'operational_steps_not_analyzed' ? 'operational steps are not traced through the repository' : undefined;
    method = str(br.method);
    riskLevel = 'not_applicable';
    riskReason = reason;
  } else {
    const legacy = legacyAssessment(br, seeds);
    ({ status, reason, method, riskLevel, riskReason } = legacy);
  }

  const entities = objList(br.affected_entities)
    .map((o) => toEntity(o, method))
    .filter((e): e is BlastEntityVm => e != null);
  const groupsByKey = new Map<string, BlastEntityGroupVm>();
  for (const e of entities) {
    const g = blastKindGroup(e.kind);
    if (!groupsByKey.has(g.key)) groupsByKey.set(g.key, { key: g.key, label: g.label, entities: [] });
    groupsByKey.get(g.key)!.entities.push(e);
  }
  const entityGroups = [...groupsByKey.values()].sort((a, b) => GROUP_ORDER.indexOf(a.key) - GROUP_ORDER.indexOf(b.key));

  const shownEntityCount = entities.length;
  const affectedCount = Math.max(num(br.affected_count) ?? shownEntityCount, shownEntityCount);
  // Older packages stored a capped list with no flag: a total above the
  // stored count is the cut. A count with no list at all is not a cut of
  // anything, and the panel says the list was not stored instead.
  const truncated = br.truncated === true || (shownEntityCount > 0 && affectedCount > shownEntityCount);

  const directCallers = objList(br.direct_callers)
    .map((o) => toEntity({ ...o, kind: 'symbol' }, method))
    .filter((e): e is BlastEntityVm => e != null);
  const directCallerCount = Math.max(num(br.direct_caller_count) ?? directCallers.length, directCallers.length);

  const changedSymbols: BlastChangedSymbolVm[] = objList(br.changed_symbols)
    .map((o): BlastChangedSymbolVm | null => {
      const name = str(o.name);
      if (!name) return null;
      const change = str(o.change) || 'modified';
      const callers = num(o.callers);
      return {
        name,
        file: str(o.file),
        kind: str(o.kind),
        change,
        oldName: str(o.old_name),
        callers,
        publicApi: o.public_api === true,
        breaksCallers: (callers ?? 0) > 0 && ['removed', 'renamed', 'signature_changed'].includes(change),
      };
    })
    .filter((s): s is BlastChangedSymbolVm => s != null);

  const areas: BlastAreaVm[] = objList(br.areas)
    .map((o): BlastAreaVm | null => {
      const area = str(o.area);
      if (!area) return null;
      return {
        area,
        areaLabel: blastAreaLabel(area),
        path: str(o.path),
        status: str(o.status) || 'unresolved',
        reason: str(o.reason),
        keys: strList(o.keys),
        entityCount: num(o.entity_count),
        riskFloor: str(o.risk_floor),
        security: o.security === true,
        operational: o.operational === true,
        factors: strList(o.factors),
      };
    })
    .filter((a): a is BlastAreaVm => a != null);

  const unresolved = objList(br.unresolved)
    .map((o) => ({ path: str(o.path) || '', reason: str(o.reason) || 'no analysis could place this path' }))
    .filter((u) => u.path);

  const modifiedTests = objList(br.modified_tests)
    .map((o) => ({ path: str(o.path) || '', kind: str(o.kind) || 'modified', detail: str(o.detail) }))
    .filter((t) => t.path);

  const nameMatchedCount = entities.filter((e) => e.evidenceClass === 'name_match').length;

  return {
    scope,
    patchScoped: scope === 'patch_paths',
    structured,
    status,
    statusLabel: statusLabel(status, reason),
    reason,
    method,
    methodLabel: blastMethodLabel(method),
    riskLevel,
    riskReason,
    riskLabel: blastRiskLabel(riskLevel, riskReason),
    pathCountRiskLevel: str(br.path_count_risk_level) || (structured ? undefined : str(br.risk_level)),
    graphRiskLevel: str(br.graph_risk_level),
    summary: structured ? str(br.summary) : undefined,
    seeds,
    changedSymbols,
    directCallers,
    directCallerCount,
    entityGroups,
    shownEntityCount,
    affectedCount,
    truncated,
    truncationLabel: truncated ? `Showing ${shownEntityCount} of ${affectedCount}` : undefined,
    nameMatchedCount,
    resolvedCount: shownEntityCount - nameMatchedCount,
    areas,
    areaCoverage: readAreaCoverage(br),
    unresolved,
    affectedTests: strList(br.affected_tests),
    dataDependentTests: strList(br.data_dependent_tests),
    testSelection: str(br.test_selection),
    modifiedTests,
    generatedFiles: strList(br.generated_files),
    publicApiChanges: strList(br.public_api_changes),
    riskFactors: strList(br.risk_factors),
    notes: blastNotes(br),
    indexedFiles: num(br.indexed_files),
    edgeCount: num(br.edge_count),
    confidence: num(br.confidence),
  };
}

/** Unknown needs a reviewer's eyes, so it reads as a warning, never calm. */
export function blastRiskTone(level: string | undefined): StatusTone {
  const l = (level || '').toLowerCase();
  if (l === 'critical' || l === 'high') return 'error';
  if (l === 'medium' || l === 'unknown' || !l) return 'warning';
  if (l === 'low') return 'info';
  return 'neutral';
}

export function blastStatusTone(status: string | undefined): StatusTone {
  switch (status) {
    case 'computed':
    case 'resolved':
      return 'success';
    case 'not_applicable':
      return 'neutral';
    default:
      return 'warning';
  }
}

/** Plain-text rendering for cards that show the package as text. */
export function blastRadiusLines(vm: BlastRadiusVm): string[] {
  const lines = [`Status: ${vm.statusLabel}`, `Risk: ${vm.riskLabel}`];
  if (vm.methodLabel) lines.push(`Method: ${vm.methodLabel}`);
  if (vm.summary) lines.push(vm.summary);
  if (vm.changedSymbols.length) {
    lines.push(
      `Changed symbols: ${vm.changedSymbols
        .map((s) => `${s.name} (${s.change.replace(/_/g, ' ')}${s.callers ? `, ${s.callers} caller${s.callers === 1 ? '' : 's'}` : ''})`)
        .join(', ')}`,
    );
  }
  if (vm.directCallerCount) {
    const shown = vm.directCallers.length < vm.directCallerCount ? ` (showing ${vm.directCallers.length})` : '';
    lines.push(`Direct callers: ${vm.directCallerCount}${shown}`);
  }
  if (vm.affectedCount || vm.shownEntityCount) {
    const hints = vm.nameMatchedCount ? `; ${vm.nameMatchedCount} name-matched` : '';
    lines.push(`Affected entities: ${vm.truncationLabel ?? vm.affectedCount}${hints}`);
    for (const g of vm.entityGroups) {
      lines.push(`  ${g.label}: ${g.entities.map((e) => e.name).join(', ')}`);
    }
  }
  if (vm.areaCoverage.length) {
    lines.push(`Areas checked: ${vm.areaCoverage.map((c) => `${c.areaLabel} (${c.statusLabel.toLowerCase()})`).join('; ')}`);
  }
  for (const a of vm.areas) {
    const flags = [a.security ? 'security-relevant' : '', a.operational ? 'operational' : ''].filter(Boolean).join(', ');
    lines.push(
      `Area ${a.areaLabel}${a.path ? ` · ${a.path}` : ''}: ${a.status.replace(/_/g, ' ')}${flags ? ` [${flags}]` : ''}${a.reason ? ` — ${a.reason}` : ''}`,
    );
  }
  for (const u of vm.unresolved) lines.push(`Unresolved: ${u.path} — ${u.reason}`);
  if (vm.affectedTests.length) lines.push(`Covering tests: ${vm.affectedTests.join(', ')}`);
  if (vm.dataDependentTests.length) lines.push(`Data-dependent tests: ${vm.dataDependentTests.join(', ')}`);
  if (vm.testSelection) lines.push(`Test selection: ${vm.testSelection}`);
  for (const t of vm.modifiedTests) lines.push(`Modified test: ${t.path} — ${t.kind.replace(/_/g, ' ')}${t.detail ? ` (${t.detail})` : ''}`);
  if (vm.generatedFiles.length) lines.push(`Generated files: ${vm.generatedFiles.join(', ')}`);
  if (vm.publicApiChanges.length) lines.push(`Public API changes: ${vm.publicApiChanges.join(', ')}`);
  for (const f of vm.riskFactors) lines.push(`Risk factor: ${f}`);
  for (const n of vm.notes) lines.push(`Note: ${n}`);
  return lines;
}

/** Names of the shown entities in the given kind groups, for scope chips. */
export function blastEntityNames(vm: BlastRadiusVm, groupKeys: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const g of vm.entityGroups) {
    if (!groupKeys.includes(g.key)) continue;
    for (const e of g.entities) {
      if (seen.has(e.name)) continue;
      seen.add(e.name);
      out.push(e.name);
    }
  }
  return out;
}

/** Short risk wording for one-line summaries: "medium", "unknown — <reason>". */
export function blastRiskText(vm: BlastRadiusVm): string {
  const l = vm.riskLevel;
  if (RISK_LABELS[l]) return l;
  if (l === 'not_applicable') return 'not applicable';
  return vm.riskReason ? `unknown — ${vm.riskReason}` : 'unknown';
}

/** One sentence for the executive summary and the investigation panel. */
export function describeBlastRadius(vm: BlastRadiusVm): string {
  if (vm.status === 'not_applicable') return `Blast radius not applicable — ${vm.reason || 'no code paths touched'}.`;
  if (vm.status === 'not_computed') {
    const risk = vm.riskLevel === 'unknown' ? 'unknown' : `${vm.riskLevel} (patched-file count)`;
    return `Blast radius not computed — ${vm.reason || 'no dependency analysis ran for this patch'}. Risk: ${risk}.`;
  }
  const via = vm.methodLabel ? ` via ${vm.methodLabel.toLowerCase()}` : '';
  const partial = vm.status === 'partial' ? ` (partial — ${vm.reason || 'part of the change could not be analysed'})` : '';
  const counts: string[] = [
    `${vm.affectedCount} affected entit${vm.affectedCount === 1 ? 'y' : 'ies'}${vm.truncated ? ` (showing ${vm.shownEntityCount})` : ''}`,
  ];
  if (vm.directCallerCount > 0) counts.push(`${vm.directCallerCount} direct caller${vm.directCallerCount === 1 ? '' : 's'}`);
  if (vm.unresolved.length > 0) counts.push(`${vm.unresolved.length} unresolved path${vm.unresolved.length === 1 ? '' : 's'}`);
  // The partial clause already carries the reason; do not repeat it.
  const risk = vm.riskLevel === 'unknown' && vm.status === 'partial' && vm.riskReason === vm.reason ? 'unknown' : blastRiskText(vm);
  return `Blast radius${via}${partial}: ${counts.join(', ')}. Risk: ${risk}.`;
}
